import { useCallback, useEffect, useRef, useState } from 'react';
import { FixedSizeList } from 'react-window';

import { useBreakpointIndex } from '../useBreakpointIndex';

import { SocialCarouselCardProps } from './SocialCarouselCard';

type TScrollProps = {
  scrollOffset: number;
  scrollUpdateWasRequested: boolean;
};

interface IScrollContainer extends FixedSizeList {
  state: {
    scrollOffset: number;
  };
}

type TUseSocialCarousel = (cards: SocialCarouselCardProps[]) => {
  beginning: boolean;
  end: boolean;
  handleNext: () => void;
  handlePrevious: () => void;
  listRef: React.MutableRefObject<FixedSizeList | null>;
  onListScroll: ({ scrollOffset }: TScrollProps) => void;
  desktopContainerPaddingLeft: number;
  cardWidth: number;
};

export const useSocialCarousel: TUseSocialCarousel = (cards) => {
  const isMobile = useBreakpointIndex() < 2;
  const [activeIndex, setActiveIndex] = useState(0);
  const [beginning, setBeginning] = useState(true);
  const [end, setEnd] = useState(false);
  const [desktopContainerPaddingLeft, setDesktopContainerPaddingLeft] =
    useState(0);
  const listRef = useRef<IScrollContainer | null>(null);
  const carouselStep = 1;
  const cardWidth = isMobile ? 250 : 375;
  const scrollContainer = listRef.current;

  useEffect(() => {
    const visibleCards = isMobile ? 1 : 5;
    setEnd(cards.length <= visibleCards);
  }, [cards]);

  useEffect(() => {
    const updatePadding = () => {
      const windowWidth = window.innerWidth;
      const value = (windowWidth - 1200) / 2 + 16;
      setDesktopContainerPaddingLeft(value);
    };

    updatePadding();
    window.addEventListener('resize', updatePadding);

    return () => window.removeEventListener('resize', updatePadding);
  }, []);

  const scrollToPosition = useCallback((newScrollPosition: number) => {
    listRef.current?.scrollTo(newScrollPosition);
  }, []);

  const handlePrevious = () => {
    if (!scrollContainer) return;

    if (activeIndex > 0) {
      const newScrollPosition = scrollContainer.state.scrollOffset - cardWidth;
      scrollToPosition(newScrollPosition);

      const isGoingToStart = activeIndex - carouselStep <= 0;

      const newIndex = isGoingToStart
        ? 0
        : Math.max(activeIndex - carouselStep, 0);

      setActiveIndex(newIndex);
      setEnd(false); // Set end to false when moving backward
    }
    if (activeIndex === 0) {
      setBeginning(true);
    }
  };

  const handleNext = () => {
    if (!scrollContainer) return;

    if (activeIndex < cards.length) {
      const additionalPadding =
        activeIndex === 0 && !isMobile ? desktopContainerPaddingLeft : 0;
      const newScrollPosition =
        scrollContainer.state.scrollOffset + cardWidth - additionalPadding;
      scrollToPosition(newScrollPosition);

      const newIndex = Math.min(activeIndex + carouselStep, cards.length - 1);

      setActiveIndex(newIndex);
      setBeginning(false);
    }

    if (activeIndex + carouselStep >= cards.length - (isMobile ? 1 : 3)) {
      setEnd(true);
    }
  };

  const onListScroll = useCallback(
    ({ scrollOffset }: TScrollProps) => {
      setBeginning(scrollOffset <= 0);
      const visibleCards = isMobile ? 1 : 5;
      setEnd(scrollOffset >= cardWidth * (cards.length - visibleCards));
      setActiveIndex(Math.round(scrollOffset / cardWidth));
    },
    [cardWidth, cards.length, isMobile],
  );

  return {
    beginning,
    desktopContainerPaddingLeft,
    end,
    handleNext,
    handlePrevious,
    listRef,
    onListScroll,
    cardWidth,
  };
};
